import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { useWorkspace2Store } from 'components/WorkspaceEditor/Workspace2Store'
import {
    ContentItemViewNodesType,
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
    SetProjectContentItemElementsResponse,
} from 'components/WorkspaceEditor/types'
import { useCallback } from 'react'
import {
    isActiveTabConsistent,
    isStaleResponse,
} from '../../../../utils/contentItemViewNodesUtils'
import { hasElementWithId } from '../../../../utils/workspaceEditorUtils'

export interface UseUpdateContentFromMutationResponseProps {
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    updateTreeViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    handleAutoExpandAfterSave: (
        nodes: Partial<ContentItemViewNodesType>[]
    ) => void
    cleanupAutoExpand: () => void
    fetchTrackChangesDataWithApolloClient: () => void
    setSaveOperationInProgress: (inProgress: boolean) => void
    setShouldTriggerNewContent?: (value: boolean) => void
    setPendingContentItemIdForAddContent: (id: string | null) => void
    initialActiveTabRef: React.MutableRefObject<number | null>
}

/**
 * Hook for processing UPDATE mutation responses directly (no GET call).
 * Handles mutation responses that may update one existing node, replace a node,
 * or return a wider Article scope with multiple existing nodes.
 *
 * Strategy 1: Multiple response nodes already exist - merge them into the current tree.
 * Strategy 2: First response node exists - update it in place.
 * Strategy 3: Response node is new/different - remove the edited node and add response nodes.
 */
export const useUpdateContentFromMutationResponse = ({
    contentItemViewNodes,
    updateTreeViewNodes,
    handleAutoExpandAfterSave,
    cleanupAutoExpand,
    fetchTrackChangesDataWithApolloClient,
    setSaveOperationInProgress,
    setShouldTriggerNewContent,
    setPendingContentItemIdForAddContent,
    initialActiveTabRef,
}: UseUpdateContentFromMutationResponseProps) => {
    const processUpdateResponse = useCallback(
        async (
            mutationResponse: SetProjectContentItemElementsResponse,
            elementId: string,
            textElement: TextContentElement,
            isEnterOperation: boolean,
            originalElementsCount: number,
            changedElementsCount: number
        ) => {
            /*
             * When Enter triggers a save before adding the next paragraph, the saved
             * paragraph can receive new element IDs or a different element structure.
             * If that happens, store the saved element ID so the add-content flow
             * creates the next paragraph after the server-confirmed content.
             */
            const wasContentStructureChanged =
                changedElementsCount !== originalElementsCount

            const { adaptMutationToQueryResponse } = await import(
                'components/WorkspaceEditor/utils/mutationResponseAdapter'
            )

            // Transform mutation response to query format
            const adaptedResponse = adaptMutationToQueryResponse(
                mutationResponse.data,
                elementId
            )

            // Get currently selected elementId (family) and activeTab from store
            const currentElementId = useElementItemStore.getState()
                .elementItemValue.elementId
            const currentActiveTab = useWorkspace2Store.getState().activeTab

            // Check if response is stale ignoring optimistic callback when elementIds are not consistent
            if (isStaleResponse(adaptedResponse, currentElementId)) {
                cleanupAutoExpand()
                setSaveOperationInProgress(false)
                return
            }

            // Check if activeTab changed during save operation (tab switch race condition)
            if (
                !isActiveTabConsistent(
                    initialActiveTabRef.current,
                    currentActiveTab
                )
            ) {
                cleanupAutoExpand()
                fetchTrackChangesDataWithApolloClient()
                setSaveOperationInProgress(false)
                return // Ignoring callback - tab switched
            }

            // Extract updated content nodes from either Product Family or Nugget response shape.
            const responseNodes =
                (adaptedResponse as GetProjectProductFamilyViewResponse)
                    .getProjectProductFamilyView?.contentItemViewNodes ||
                (adaptedResponse as GetProjectNuggetContentViewResponse)
                    .getProjectNugget?.contentItemViewNodes ||
                []

            const existingResponseNodes = responseNodes.filter(
                (responseNode) =>
                    contentItemViewNodes.some(
                        (node) => node.id === responseNode.id
                    )
            )

            if (existingResponseNodes.length > 1) {
                // Article responses can include the Article plus child nodes; merge each matching node in place.
                const responseNodesById = new Map(
                    responseNodes.map((node) => [node.id, node])
                )
                const updatedContentItemViewNodes = contentItemViewNodes.map(
                    (node) => {
                        const responseNode = responseNodesById.get(node.id)
                        if (!responseNode) {
                            return node
                        }

                        return {
                            ...node,
                            ...responseNode,
                            // Preserve BP flags from existing node if server response doesn't include them
                            isBestPractice:
                                responseNode?.isBestPractice ??
                                node?.isBestPractice,
                            isGlobalBestPractice:
                                responseNode?.isGlobalBestPractice ??
                                node?.isGlobalBestPractice,
                            drawingDesignation:
                                responseNode?.drawingDesignation ||
                                node?.drawingDesignation,
                        }
                    }
                )
                const existingNodeIds = new Set(
                    contentItemViewNodes.map((node) => node.id)
                )
                const updatedNodes = [
                    ...updatedContentItemViewNodes,
                    ...responseNodes
                        .filter((node) => !existingNodeIds.has(node.id))
                        .map((node) => ({
                            ...node,
                            drawingDesignation: node?.drawingDesignation,
                        })),
                ]
                const responseNodeForTextElement =
                    responseNodes.find((node) =>
                        node.elements?.some((element) =>
                            hasElementWithId(element, textElement.id)
                        )
                    ) ?? responseNodes[0]
                const firstElement = responseNodeForTextElement?.elements?.[0]
                const firstContentItemId = firstElement?.id

                // For keyed paragraphs, we need the LAST element (value part), not the first (key part)
                const lastElement =
                    responseNodeForTextElement?.elements?.[
                        responseNodeForTextElement.elements.length - 1
                    ]
                const lastContentItemId = lastElement?.id

                // For keyed paragraphs, we have multiple elements and need to add after the LAST one
                const hasMultipleElements =
                    (responseNodeForTextElement?.elements?.length || 0) > 1

                if (isEnterOperation && wasContentStructureChanged) {
                    // For keyed paragraphs (multiple elements), use LAST element to add content after the value part
                    // For regular content, use first element
                    const targetElementId = hasMultipleElements
                        ? lastContentItemId
                        : firstContentItemId
                    setPendingContentItemIdForAddContent(
                        targetElementId || null
                    )
                } else {
                    setPendingContentItemIdForAddContent(null)
                }

                if (updatedNodes.length > 0) {
                    handleAutoExpandAfterSave(updatedNodes)
                    updateTreeViewNodes(updatedNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }

                setSaveOperationInProgress(false)
                return
            }

            // Single-node response: update matching node in place or replace the edited node.
            const findContentItemViewNode = contentItemViewNodes.find(
                (node) => node.id === responseNodes[0]?.id
            )

            if (findContentItemViewNode && responseNodes.length > 0) {
                // Strategy 2: Node exists with same ID - update it in place.
                const updatedNode = {
                    ...findContentItemViewNode,
                    ...responseNodes[0],
                    // Preserve BP flags from existing node if server response doesn't include them
                    isBestPractice:
                        responseNodes[0]?.isBestPractice ??
                        findContentItemViewNode?.isBestPractice,
                    isGlobalBestPractice:
                        responseNodes[0]?.isGlobalBestPractice ??
                        findContentItemViewNode?.isGlobalBestPractice,
                    drawingDesignation:
                        responseNodes[0]?.drawingDesignation ||
                        findContentItemViewNode?.drawingDesignation,
                }
                const updatedContentItemViewNodes = contentItemViewNodes.map(
                    (node) => (node.id === updatedNode.id ? updatedNode : node)
                )
                const firstElement = responseNodes[0]?.elements?.[0]
                const firstContentItemId = firstElement?.id

                // For keyed paragraphs, we need the LAST element (value part), not the first (key part)
                const lastElement =
                    responseNodes[0]?.elements?.[
                        responseNodes[0].elements.length - 1
                    ]
                const lastContentItemId = lastElement?.id

                // For keyed paragraphs, we have multiple elements and need to add after the LAST one
                const hasMultipleElements =
                    (responseNodes[0]?.elements?.length || 0) > 1

                /*
                 * COORDINATION STEP 1: Tell the "create new paragraph" handler which paragraph to create after
                 *
                 * When user presses Enter and the save changes the paragraph structure,
                 * we store the SAVED paragraph's ID so the Enter handler knows where to create the new paragraph.
                 *
                 * EXAMPLE:
                 * 1. User has paragraph with temporary ID "temp-123"
                 * 2. User presses Enter → triggers save
                 * 3. Save completes → paragraph now has real ID "real-456"
                 * 4. We store "real-456" in pendingContentItemIdForAddContent
                 * 5. Enter handler reads this ID and creates new paragraph after "real-456"
                 *
                 * Without this fix: Enter handler would try to create after "temp-123" which doesn't exist anymore!
                 */
                if (isEnterOperation && wasContentStructureChanged) {
                    // For keyed paragraphs (multiple elements), use LAST element to add content after the value part
                    // For regular content, use first element
                    const targetElementId = hasMultipleElements
                        ? lastContentItemId
                        : firstContentItemId
                    setPendingContentItemIdForAddContent(
                        targetElementId || null
                    )
                } else {
                    setPendingContentItemIdForAddContent(null)
                }
                // Auto-expand newly added article if applicable
                if (updatedContentItemViewNodes.length > 0) {
                    handleAutoExpandAfterSave(updatedContentItemViewNodes)
                    updateTreeViewNodes(updatedContentItemViewNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }
            } else {
                // Strategy 3: Node not found or different structure - remove old and add new.
                // Find the node containing the textElement.id being updated
                const nodeToRemove = contentItemViewNodes.find((node) =>
                    node.elements?.some((element) =>
                        hasElementWithId(element, textElement.id)
                    )
                )

                const oldParentId = nodeToRemove?.id
                const newParentId = responseNodes[0]?.id
                // Keyed conversion replaces P_old with P_new (new id + new dtvItemId).
                // Re-anchor any children still pointing at P_old so TreeDataMapper can find them.
                const hasParentChanged = !!(
                    oldParentId &&
                    newParentId &&
                    oldParentId !== newParentId
                )

                const filteredNodes = nodeToRemove
                    ? contentItemViewNodes
                          .filter((node) => node.id !== nodeToRemove.id)
                          .map((node) => {
                              const shouldReanchor =
                                  hasParentChanged &&
                                  (node.dtvContentItemParentId ===
                                      oldParentId ||
                                      // isDirectChildNode path: dtvContentItemParentId is null,
                                      // child is linked via dtvParentId → P_old.dtvItemId.
                                      // P_new gets a new dtvItemId, breaking that implicit link.
                                      (!!nodeToRemove.dtvItemId &&
                                          !node.dtvContentItemParentId &&
                                          node.dtvParentId ===
                                              nodeToRemove.dtvItemId))
                              return shouldReanchor
                                  ? {
                                        ...node,
                                        dtvContentItemParentId: newParentId,
                                    }
                                  : node
                          })
                    : contentItemViewNodes

                // Preserve BP flags from removed node when adding new nodes from response
                const responseNodesWithBPFlags = responseNodes.map((node) => ({
                    ...node,
                    // Preserve BP flags from removed node if response doesn't include them
                    isBestPractice:
                        node?.isBestPractice ?? nodeToRemove?.isBestPractice,
                    isGlobalBestPractice:
                        node?.isGlobalBestPractice ??
                        nodeToRemove?.isGlobalBestPractice,
                    // Preserve drawingDesignation — content save mutations don't modify it
                    drawingDesignation:
                        node?.drawingDesignation ||
                        nodeToRemove?.drawingDesignation,
                }))

                // Add new nodes (if empty, this effectively removes the old node)
                const updatedNodes = [
                    ...filteredNodes,
                    ...responseNodesWithBPFlags,
                ]
                const firstElement = responseNodes[0]?.elements?.[0]
                const firstContentItemId = firstElement?.id

                // For keyed paragraphs, we need the LAST element (value part), not the first (key part)
                const lastElement =
                    responseNodes[0]?.elements?.[
                        responseNodes[0].elements.length - 1
                    ]
                const lastContentItemId = lastElement?.id

                /*
                 * COORDINATION STEP 2: Same as Step 1, but for newly created paragraphs
                 *
                 * This handles the case where save creates a NEW paragraph (not updating existing).
                 * Same problem: temporary ID → real ID, need to track the real ID.
                 *
                 * EXAMPLE:
                 * 1. User adds new text content (gets temporary ID)
                 * 2. User presses Enter → triggers save
                 * 3. Save creates NEW paragraph with real ID from server
                 * 4. We store the real ID so Enter handler knows which paragraph it just created
                 * 5. Next Enter can create content after the correct paragraph
                 */
                if (isEnterOperation) {
                    // For keyed paragraphs, we have multiple elements and need to add after the LAST one
                    const hasMultipleElements =
                        (responseNodes[0]?.elements?.length || 0) > 1
                    // For keyed paragraphs (multiple elements), use LAST element to add content after the value part
                    // For regular content, use first element
                    const targetElementId = hasMultipleElements
                        ? lastContentItemId
                        : firstContentItemId
                    setPendingContentItemIdForAddContent(
                        targetElementId || null
                    )
                }

                // Auto-expand newly added article if applicable
                if (updatedNodes.length > 0) {
                    handleAutoExpandAfterSave(updatedNodes)
                    updateTreeViewNodes(updatedNodes)
                    setShouldTriggerNewContent?.(isEnterOperation)
                }
            }

            // IMPORTANT: Clear save operation flag BEFORE triggering new content
            // Otherwise canAddContent will reject due to isSaveOperationInProgress
            setSaveOperationInProgress(false)
        },
        [
            contentItemViewNodes,
            updateTreeViewNodes,
            handleAutoExpandAfterSave,
            cleanupAutoExpand,
            fetchTrackChangesDataWithApolloClient,
            setSaveOperationInProgress,
            setShouldTriggerNewContent,
            setPendingContentItemIdForAddContent,
            initialActiveTabRef,
        ]
    )

    return { processUpdateResponse }
}
